import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Paper, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay, EmptyState, Code } from 'dashboard/components/common';

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

export default function ErrorsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.adminListErrors({ limit: 200 })
      .then((res) => { if (!cancelled) setRows(Array.isArray(res) ? res : []); })
      .catch((err) => toast.error(err?.message || 'Failed to load errors', { toastId: 'admin-errors' }))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='12px'>
        <Typography variant='body2' color='text.secondary'>
          Server errors from the last 7 days. Older entries are pruned automatically.
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {rows.length} entries
        </Typography>
      </Stack>

      <LoadingOverlay loading={loading} minHeight={240}>
        {rows.length === 0 && !loading ? (
          <EmptyState>No errors recorded in the last 7 days. Quiet week.</EmptyState>
        ) : (
          <Stack spacing='8px'>
            {rows.map((e) => (
              <Paper key={e._id} elevation={0} sx={{ borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}>
                <Accordion disableGutters elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: '14px' }}>
                    <Stack direction='row' justifyContent='space-between' alignItems='center' width='100%' spacing='10px'>
                      <Stack direction='row' spacing='8px' alignItems='center' sx={{ flex: 1, minWidth: 0 }}>
                        <Chip
                          size='small'
                          color={e.level === 'error' ? 'error' : 'warning'}
                          label={e.level || 'error'}
                        />
                        {e.status && <Chip size='small' label={String(e.status)} />}
                        <Typography fontWeight='600' noWrap sx={{ flex: 1, minWidth: 0 }}>
                          {e.message}
                        </Typography>
                      </Stack>
                      <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                        {fmtTime(e.createdAt)}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: '14px', pb: '14px' }}>
                    {(e.method || e.route) && (
                      <Typography variant='body2' mb='6px'>
                        <Code>{[e.method, e.route].filter(Boolean).join(' ')}</Code>
                      </Typography>
                    )}
                    {e.user_id && (
                      <Typography variant='caption' color='text.secondary' display='block' mb='8px'>
                        User: {String(e.user_id)}
                      </Typography>
                    )}
                    {e.stack && (
                      <Box component='pre' sx={{
                        m: 0, p: '12px', borderRadius: '8px',
                        bgcolor: 'action.hover',
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 320,
                        overflow: 'auto',
                      }}>
                        {e.stack}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Paper>
            ))}
          </Stack>
        )}
      </LoadingOverlay>
    </Box>
  );
}
